import React, { useState, useMemo } from 'react';
import { Card, Button } from 'react-bootstrap';
import { FaGlobe } from 'react-icons/fa';

const WorldMapView = ({ resources, onViewArchitecture }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // CALIBRATED coordinates based on user clicks (Feb 5, 2026)
  // Anchor points:
  // us-east-1 (Virginia): 25.5%, 53.3%
  // eu-central-1 (Frankfurt): 50.4%, 44.3%
  // ap-southeast-2 (Sydney): 88.5%, 84.3%
  // me-central-1 (Dubai): 61.5%, 59.5%
  
  const regionCoordinates = {
    // US Regions (based on Virginia anchor at 25.5%, 53.3%)
    'us-east-1': { x: 25.5, y: 53.3, name: 'N. Virginia' },      // Anchor point
    'us-east-2': { x: 26.8, y: 51.8, name: 'Ohio' },             // Slightly west/north
    'us-west-1': { x: 13.8, y: 54.5, name: 'N. California' },    // West coast, bit south
    'us-west-2': { x: 12.2, y: 50.5, name: 'Oregon' },           // Pacific Northwest
    
    // Canada (north of US)
    'ca-central-1': { x: 26.2, y: 47.5, name: 'Montreal' },      // East Canada, north of Virginia
    'ca-west-1': { x: 13.2, y: 47.0, name: 'Calgary' },          // West Canada
    
    // Europe (based on Frankfurt anchor at 50.4%, 44.3%)
    'eu-west-1': { x: 46.8, y: 45.5, name: 'Ireland' },          // West of UK
    'eu-west-2': { x: 48.2, y: 46.0, name: 'London' },           // UK
    'eu-west-3': { x: 49.8, y: 48.5, name: 'Paris' },            // France
    'eu-central-1': { x: 50.4, y: 44.3, name: 'Frankfurt' },     // Germany (anchor)
    'eu-central-2': { x: 51.8, y: 49.0, name: 'Zurich' },        // Switzerland
    'eu-north-1': { x: 51.5, y: 38.5, name: 'Stockholm' },       // Sweden (far north)
    'eu-south-1': { x: 51.2, y: 51.5, name: 'Milan' },           // Italy
    'eu-south-2': { x: 47.8, y: 53.0, name: 'Spain' },           // Spain
    
    // Middle East (based on Dubai anchor at 61.5%, 59.5%)
    'me-central-1': { x: 61.5, y: 59.5, name: 'UAE' },           // Dubai (anchor)
    'me-south-1': { x: 60.2, y: 58.5, name: 'Bahrain' },         // Near Dubai, slightly west
    'il-central-1': { x: 57.2, y: 56.2, name: 'Tel Aviv' },      // Israel, west of Dubai
    
    // Asia Pacific - South Asia (between Middle East and Southeast)
    'ap-south-1': { x: 67.0, y: 60.5, name: 'Mumbai' },          // India, east of Dubai
    'ap-south-2': { x: 69.0, y: 61.5, name: 'Hyderabad' },       // India, SE of Mumbai
    
    // Asia Pacific - Southeast (interpolating toward Sydney anchor)
    'ap-southeast-1': { x: 75.5, y: 65.5, name: 'Singapore' },   // Singapore, equator
    'ap-southeast-2': { x: 88.5, y: 84.3, name: 'Sydney' },      // Sydney (anchor)
    'ap-southeast-3': { x: 76.5, y: 68.0, name: 'Jakarta' },     // Indonesia, south of equator
    'ap-southeast-4': { x: 87.8, y: 82.5, name: 'Melbourne' },   // South Australia, near Sydney
    
    // Asia Pacific - East Asia (between Southeast Asia and far east)
    'ap-northeast-1': { x: 84.5, y: 55.5, name: 'Tokyo' },       // Japan
    'ap-northeast-2': { x: 81.8, y: 54.8, name: 'Seoul' },       // South Korea
    'ap-northeast-3': { x: 83.5, y: 56.2, name: 'Osaka' },       // Japan, south of Tokyo
    'ap-east-1': { x: 79.5, y: 60.5, name: 'Hong Kong' },        // Hong Kong
    
    // South America (west of Africa, southern hemisphere)
    'sa-east-1': { x: 32.5, y: 72.5, name: 'São Paulo' },        // Brazil
    
    // Africa
    'af-south-1': { x: 52.2, y: 81.5, name: 'Cape Town' }        // South Africa, southern tip
  };

  const regionData = useMemo(() => {
    const regions = {};
    
    if (!resources || resources.length === 0) return regions;
    
    resources.forEach(resource => {
      const region = resource.region || 'global';
      if (!regions[region]) {
        regions[region] = {
          count: 0,
          types: {}
        };
      }
      regions[region].count++;
      regions[region].types[resource.type] = (regions[region].types[resource.type] || 0) + 1;
    });
    
    return regions;
  }, [resources]);

  const maxCount = Math.max(...Object.values(regionData).map(r => r.count), 1);

  if (!resources || resources.length === 0) {
    return null;
  }

  const getCircleRadius = (count) => {
    const minRadius = 8;
    const maxRadius = 30;
    return minRadius + (count / maxCount) * (maxRadius - minRadius);
  };

  const handleRegionClick = (region, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPos({ 
      x: rect.left + rect.width / 2, 
      y: rect.top - 10
    });
    setSelectedRegion(region);
  };

  const handleCloseTooltip = () => {
    setSelectedRegion(null);
  };

  const getTopResourceTypes = (types) => {
    return Object.entries(types)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([type, count]) => ({ type, count }));
  };

  return (
    <Card style={{
      background: 'white',
      borderRadius: '12px',
      border: '1px solid #e9ecef',
      marginBottom: '24px',
      overflow: 'hidden'
    }}>
      <Card.Body style={{ padding: '24px' }}>
        <div className="d-flex align-items-center mb-3">
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: 'rgba(109, 91, 255, 0.1)',
            color: '#6D5BFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            marginRight: '12px'
          }}>
            <FaGlobe />
          </div>
          <div>
            <h5 className="mb-0" style={{ fontWeight: '600', color: '#1e293b' }}>
              Global Resource Map
            </h5>
            <small style={{ color: '#64748b' }}>
              Geographic distribution across AWS regions
            </small>
          </div>
        </div>

        <div style={{ position: 'relative', width: '100%', paddingBottom: '66%', background: '#f8f9fa', borderRadius: '8px' }}>
          <img 
            src="/world.svg" 
            alt="World Map" 
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              opacity: 0.3,
              filter: 'grayscale(100%)'
            }} 
          />
          
          {/* Region markers overlay */}
          {Object.entries(regionData).map(([region, data]) => {
            const coords = regionCoordinates[region];
            if (!coords) {
              console.warn(`No coordinates found for region: ${region}`);
              return null;
            }

            const radius = getCircleRadius(data.count);

            return (
              <div
                key={region}
                style={{
                  position: 'absolute',
                  left: `${coords.x}%`,
                  top: `${coords.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: `${radius * 2}px`,
                  height: `${radius * 2}px`,
                  borderRadius: '50%',
                  background: selectedRegion === region ? '#342ead' : '#6D5BFF',
                  opacity: selectedRegion === region ? 1 : 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: radius > 15 ? '12px' : '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: selectedRegion === region 
                    ? '0 0 20px rgba(52, 46, 173, 0.8)' 
                    : '0 0 15px rgba(109, 91, 255, 0.4)',
                  transition: 'all 0.2s',
                  zIndex: 10,
                  border: selectedRegion === region ? '2px solid white' : 'none'
                }}
                onClick={(e) => handleRegionClick(region, e)}
              >
                {data.count}
              </div>
            );
          })}

          {/* Tooltip */}
          {selectedRegion && regionData[selectedRegion] && (
            <div
              style={{
                position: 'fixed',
                left: tooltipPos.x,
                top: tooltipPos.y - 10,
                transform: 'translate(-50%, -100%)',
                background: 'white',
                border: '2px solid #342ead',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: '200px',
                pointerEvents: 'auto'
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div style={{ fontWeight: '600', color: '#1e293b' }}>
                  {regionCoordinates[selectedRegion]?.name || selectedRegion}
                </div>
                <button
                  onClick={handleCloseTooltip}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    lineHeight: 1,
                    padding: 0,
                    marginLeft: '8px'
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '8px' }}>
                {selectedRegion}
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6D5BFF', marginBottom: '8px' }}>
                {regionData[selectedRegion].count} resources
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginBottom: '8px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                  TOP RESOURCE TYPES
                </div>
                {getTopResourceTypes(regionData[selectedRegion].types).map(({ type, count }) => (
                  <div key={type} style={{ fontSize: '0.875rem', color: '#1e293b', marginBottom: '2px' }}>
                    <span style={{ color: '#6D5BFF', fontWeight: '600' }}>{count}</span> {type}
                  </div>
                ))}
              </div>
              {onViewArchitecture && (
                <Button 
                  size="sm"
                  style={{
                    width: '100%',
                    background: '#342ead',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewArchitecture(selectedRegion);
                    handleCloseTooltip();
                  }}
                >
                  View Architecture Diagram
                </Button>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default WorldMapView;
